import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Database,
  Plus,
  Search,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useVendors } from "@/hooks/queries";
import {
  useCpeMappings,
  useCreateVendorMapping,
  useMapToExisting,
  useResolveConflict,
  type ConflictRow,
  type UnmappedRow,
} from "@/hooks/useCpeMappings";
import type { Vendor } from "@/lib/types";

/**
 * /admin/cpe-mappings — Phase C C3 T14.
 *
 * Three sections:
 *   1. Stats banner (mapped / unmapped queue depth / conflict count)
 *   2. Unmapped queue panel (paginated, sorted by occurrence_count DESC)
 *      - Each row → "Map to existing" or "Create new vendor" actions
 *   3. Conflicts panel (radio-pick from candidate vendor list — Captain decision 2)
 *
 * Server contract: apps/api/src/routes/admin/cpe-mappings.routes.ts.
 */

// ─── Status banner (replaces toast — keep it simple) ─────────────────────────

interface StatusMessage {
  kind: "success" | "error";
  text: string;
}

function StatusBanner({
  status,
  onDismiss,
}: {
  status: StatusMessage | null;
  onDismiss: () => void;
}) {
  if (!status) return null;
  const isErr = status.kind === "error";
  return (
    <div
      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
        isErr
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-emerald-200 bg-emerald-500/10 text-emerald-700"
      }`}
      role="status"
    >
      <span>{status.text}</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ─── KPI tile ────────────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  warn,
}: {
  label: string;
  value: number | string;
  warn?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={`text-2xl font-bold tabular-nums ${
            warn ? "text-amber-500" : ""
          }`}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Map-to-existing modal ───────────────────────────────────────────────────

interface MapModalProps {
  cpeVendor: string;
  occurrenceCount: number;
  vendors: Vendor[] | undefined;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function MapModal({
  cpeVendor,
  occurrenceCount,
  vendors,
  onClose,
  onSuccess,
  onError,
}: MapModalProps) {
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<Vendor | null>(null);
  const map = useMapToExisting();

  const filtered = useMemo(() => {
    if (!vendors) return [];
    const q = search.trim().toLowerCase();
    if (!q) return vendors.slice(0, 25);
    return vendors
      .filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.aliases?.some((a) => a.toLowerCase().includes(q)),
      )
      .slice(0, 25);
  }, [vendors, search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!chosen) return;
    try {
      const res = await map.mutateAsync({
        cpeVendor,
        vendorId: chosen.id,
        deleteFromUnmapped: true,
      });
      onSuccess(
        `Mapped "${cpeVendor}" → ${res.mapping.vendorName} (${occurrenceCount} CVE${
          occurrenceCount === 1 ? "" : "s"
        }).`,
      );
      onClose();
    } catch (err) {
      onError(
        err instanceof Error ? err.message : "Failed to save mapping",
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-lg bg-background border shadow-xl p-6">
        <h2 className="text-base font-semibold mb-1">
          Map CPE vendor to existing
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          Token <code className="font-mono">{cpeVendor}</code>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="vendor-search">Vendor</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="vendor-search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setChosen(null);
                }}
                placeholder="Search vendors by name or alias…"
                className="pl-8"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border">
            {filtered.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                No vendors match.
              </div>
            ) : (
              <ul role="listbox" aria-label="Vendor results">
                {filtered.map((v) => {
                  const selected = chosen?.id === v.id;
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => setChosen(v)}
                        className={`flex w-full items-center justify-between border-b px-3 py-2 text-left text-sm transition-colors hover:bg-muted/30 last:border-0 ${
                          selected ? "bg-muted" : ""
                        }`}
                      >
                        <div>
                          <div className="font-medium">{v.name}</div>
                          {v.aliases && v.aliases.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {v.aliases.slice(0, 4).join(", ")}
                              {v.aliases.length > 4 ? ", …" : ""}
                            </div>
                          )}
                        </div>
                        {selected && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!chosen || map.isPending}
            >
              {map.isPending ? "Saving…" : "Save mapping"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Create-new-vendor modal ─────────────────────────────────────────────────

interface CreateVendorModalProps {
  cpeVendor: string;
  occurrenceCount: number;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function CreateVendorModal({
  cpeVendor,
  occurrenceCount,
  onClose,
  onSuccess,
  onError,
}: CreateVendorModalProps) {
  const [name, setName] = useState("");
  const [aliasesRaw, setAliasesRaw] = useState("");
  const create = useCreateVendorMapping();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const aliases = aliasesRaw
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);
    try {
      await create.mutateAsync({
        cpeVendor,
        vendorName: trimmed,
        vendorAliases: aliases.length > 0 ? aliases : undefined,
      });
      onSuccess(
        `Created vendor "${trimmed}" + mapped "${cpeVendor}" (${occurrenceCount} CVE${
          occurrenceCount === 1 ? "" : "s"
        }).`,
      );
      onClose();
    } catch (err) {
      onError(
        err instanceof Error ? err.message : "Failed to create vendor",
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-lg bg-background border shadow-xl p-6">
        <h2 className="text-base font-semibold mb-1">Create new vendor</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Token <code className="font-mono">{cpeVendor}</code>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-vendor-name">Name *</Label>
            <Input
              id="new-vendor-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-vendor-aliases">
              Aliases (comma-separated)
            </Label>
            <Input
              id="new-vendor-aliases"
              placeholder="Alias 1, Alias 2"
              value={aliasesRaw}
              onChange={(e) => setAliasesRaw(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Alternative names used in article matching.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!name.trim() || create.isPending}
            >
              {create.isPending ? "Creating…" : "Create + map"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Unmapped queue panel ────────────────────────────────────────────────────

const PAGE_SIZE = 50;

interface UnmappedPanelProps {
  rows: UnmappedRow[];
  page: number;
  hasMore: boolean;
  onPrev: () => void;
  onNext: () => void;
  onMapExisting: (row: UnmappedRow) => void;
  onCreateNew: (row: UnmappedRow) => void;
  isLoading: boolean;
}

function UnmappedPanel({
  rows,
  page,
  hasMore,
  onPrev,
  onNext,
  onMapExisting,
  onCreateNew,
  isLoading,
}: UnmappedPanelProps) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-4 w-4" />
          Unmapped CPE vendors
        </CardTitle>
        <div className="flex items-center gap-1 text-sm">
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0"
            onClick={onPrev}
            disabled={page <= 1 || isLoading}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-muted-foreground tabular-nums px-2">
            Page {page}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0"
            onClick={onNext}
            disabled={!hasMore || isLoading}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No unmapped CPE vendors.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/20 text-muted-foreground text-left">
                  <th className="px-4 py-2 font-medium">CPE vendor</th>
                  <th className="px-4 py-2 font-medium tabular-nums">
                    Occurrences
                  </th>
                  <th className="px-4 py-2 font-medium">Sample CVEs</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.cpeVendor}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.cpeVendor}
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      {r.occurrenceCount}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {r.sampleCveIds.slice(0, 5).map((cve) => (
                          <Link
                            key={cve}
                            to={`/vulnerabilities/${cve}`}
                            className="text-primary hover:underline"
                          >
                            {cve}
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => onMapExisting(r)}
                        >
                          Map to existing
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          className="h-7 text-xs"
                          onClick={() => onCreateNew(r)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Create new
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Conflicts panel ─────────────────────────────────────────────────────────

interface ConflictsPanelProps {
  rows: ConflictRow[];
  onResolve: (cpeVendor: string, chosenVendorId: string) => Promise<void>;
  isLoading: boolean;
}

function ConflictsPanel({ rows, onResolve, isLoading }: ConflictsPanelProps) {
  // Per-row chosen candidate (radio-pick — Captain decision 2).
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [pendingCpe, setPendingCpe] = useState<string | null>(null);

  async function handleResolve(cpe: string) {
    const chosen = picks[cpe];
    if (!chosen) return;
    setPendingCpe(cpe);
    try {
      await onResolve(cpe, chosen);
      setPicks((prev) => {
        const next = { ...prev };
        delete next[cpe];
        return next;
      });
    } finally {
      setPendingCpe(null);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Conflicts ({rows.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No conflicts.
          </div>
        ) : (
          <div className="divide-y">
            {rows.map((c) => (
              <div key={c.cpeVendor} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <code className="font-mono text-sm">{c.cpeVendor}</code>
                  <Badge variant="outline" className="text-xs">
                    {c.candidates.length} candidates
                  </Badge>
                </div>
                <fieldset
                  className="space-y-1.5"
                  aria-label={`Candidates for ${c.cpeVendor}`}
                >
                  {c.candidates.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">
                      No candidates found — re-seed the map.
                    </p>
                  ) : (
                    c.candidates.map((cand) => (
                      <label
                        key={cand.id}
                        className="flex items-center gap-2 text-sm cursor-pointer rounded px-2 py-1 hover:bg-muted/30"
                      >
                        <input
                          type="radio"
                          name={`conflict-${c.cpeVendor}`}
                          value={cand.id}
                          checked={picks[c.cpeVendor] === cand.id}
                          onChange={() =>
                            setPicks((prev) => ({
                              ...prev,
                              [c.cpeVendor]: cand.id,
                            }))
                          }
                          className="accent-primary"
                        />
                        <span>{cand.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {cand.id.slice(0, 8)}…
                        </span>
                      </label>
                    ))
                  )}
                </fieldset>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => handleResolve(c.cpeVendor)}
                    disabled={
                      !picks[c.cpeVendor] || pendingCpe === c.cpeVendor
                    }
                  >
                    {pendingCpe === c.cpeVendor ? "Resolving…" : "Resolve"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function CpeMappingsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusMessage | null>(null);

  const {
    data,
    isLoading: mappingsLoading,
    isError,
    error,
  } = useCpeMappings(page, PAGE_SIZE);
  const { data: vendors } = useVendors();
  const resolve = useResolveConflict();

  // Modal state
  const [mapModal, setMapModal] = useState<UnmappedRow | null>(null);
  const [createModal, setCreateModal] = useState<UnmappedRow | null>(null);

  function flashSuccess(text: string) {
    setStatus({ kind: "success", text });
  }
  function flashError(text: string) {
    setStatus({ kind: "error", text });
  }

  async function handleResolveConflict(
    cpeVendor: string,
    chosenVendorId: string,
  ) {
    try {
      await resolve.mutateAsync({ cpeVendor, chosenVendorId });
      flashSuccess(`Resolved conflict for "${cpeVendor}".`);
    } catch (err) {
      flashError(
        err instanceof Error ? err.message : "Failed to resolve conflict",
      );
    }
  }

  const stats = data?.stats;
  const unmapped = data?.unmapped;
  const conflicts = data?.conflicts ?? [];
  const hasMore =
    !!unmapped && unmapped.rows.length === unmapped.pageSize;

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">CPE vendor mappings</h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Triage unmapped CPE vendor tokens and resolve seeder conflicts. Phase C C3.
        </p>
      </div>

      <StatusBanner status={status} onDismiss={() => setStatus(null)} />

      {/* Stats banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiTile
          label="Mapped tokens"
          value={mappingsLoading ? "—" : (stats?.totalMapped ?? 0)}
        />
        <KpiTile
          label="Unmapped (queue)"
          value={mappingsLoading ? "—" : (stats?.totalUnmapped ?? 0)}
          warn={(stats?.totalUnmapped ?? 0) > 0}
        />
        <KpiTile
          label="Conflicts"
          value={mappingsLoading ? "—" : (stats?.conflictCount ?? 0)}
          warn={(stats?.conflictCount ?? 0) > 0}
        />
      </div>

      {isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Failed to load mappings: {error instanceof Error ? error.message : "unknown"}
        </div>
      )}

      <UnmappedPanel
        rows={unmapped?.rows ?? []}
        page={page}
        hasMore={hasMore}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
        onMapExisting={(r) => setMapModal(r)}
        onCreateNew={(r) => setCreateModal(r)}
        isLoading={mappingsLoading}
      />

      <ConflictsPanel
        rows={conflicts}
        onResolve={handleResolveConflict}
        isLoading={mappingsLoading}
      />

      {mapModal && (
        <MapModal
          cpeVendor={mapModal.cpeVendor}
          occurrenceCount={mapModal.occurrenceCount}
          vendors={vendors}
          onClose={() => setMapModal(null)}
          onSuccess={flashSuccess}
          onError={flashError}
        />
      )}
      {createModal && (
        <CreateVendorModal
          cpeVendor={createModal.cpeVendor}
          occurrenceCount={createModal.occurrenceCount}
          onClose={() => setCreateModal(null)}
          onSuccess={flashSuccess}
          onError={flashError}
        />
      )}
    </div>
  );
}

export default CpeMappingsPage;
